import { supabase } from '../lib/supabase';
import { DEMO_ERROR_MESSAGE, isDemoUser } from '../utils/demoMode';

// Map entity type to column name
const getEntityColumn = (entityType) => {
  const columnMap = {
    site: 'site_id',
    task: 'task_id',
    snag: 'snag_id',
    incident: 'incident_id',
    near_miss: 'near_miss_id',
  };
  return columnMap?.[entityType] || null;
};

export const notesService = {
  /**
   * Get notes for a specific entity.
   * entityType: 'site' | 'task' | 'snag' | 'incident' | 'near_miss'
   * entityId: UUID of the entity
   * userRole: 'admin' | 'site_manager' | 'worker' | 'client'
   */
  async getNotes(entityType, entityId, userRole = 'worker') {
    try {
      const column = getEntityColumn(entityType);
      if (!column) throw new Error(`Invalid entity type: ${entityType}`);

      let query = supabase
        ?.from('notes')
        ?.select('*, creator:user_profiles!notes_created_by_fkey(id, full_name, role)')
        ?.eq(column, entityId)
        ?.order('created_at', { ascending: true });

      // Filter by visibility based on role
      if (userRole === 'client') {
        query = query?.eq('visibility', 'client_shared');
      } else if (userRole === 'worker') {
        query = query?.in('visibility', ['team', 'client_shared']);
      } else if (userRole === 'site_manager') {
        query = query?.in('visibility', ['team', 'client_shared']);
      }
      // admin sees all — no filter

      const { data, error } = await query;
      if (error) throw error;
      return { data: data || [], error: null };
    } catch (err) {
      console.error('notesService.getNotes error:', err);
      return { data: [], error: err };
    }
  },

  /**
   * Add a new note to an entity.
   */
  async addNote({ entityType, entityId, noteText, visibility = 'team', photoFile = null, companyId, mentions = [] }) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data: profile } = await supabase
        ?.from('user_profiles')
        ?.select('full_name, role')
        ?.eq('id', user?.id)
        ?.maybeSingle();

      // Demo mode guard — surface an explicit error instead of fake success.
      if (isDemoUser(user?.email)) {
        window.dispatchEvent(new CustomEvent('demo-mode-action'));
        return { data: null, error: { message: DEMO_ERROR_MESSAGE } };
      }

      const column = getEntityColumn(entityType);
      if (!column) throw new Error(`Invalid entity type: ${entityType}`);

      let photoUrl = null;

      // Upload photo if provided
      if (photoFile) {
        const ext = photoFile?.name?.split('.')?.pop() || 'jpg';
        const path = `${user?.id}/${entityType}/${entityId}/${Date.now()}.${ext}`;
        const { data: uploadData, error: uploadError } = await supabase?.storage
          ?.from('note-photos')
          ?.upload(path, photoFile, { upsert: false });
        if (uploadError) {
          console.warn('Note photo upload failed:', uploadError?.message);
        } else {
          const { data: urlData } = await supabase?.storage
            ?.from('note-photos')
            ?.createSignedUrl(path, 60 * 60 * 24 * 365);
          photoUrl = urlData?.signedUrl || null;
        }
      }

      const insertPayload = {
        company_id: companyId,
        [column]: entityId,
        note_text: noteText,
        note_type: visibility === 'client_shared' ? 'shared' : 'internal',
        visibility,
        photo_url: photoUrl,
        created_by: user?.id,
        created_by_name: profile?.full_name || null,
        mentions: mentions?.length > 0 ? mentions : [],
      };

      const { data, error } = await supabase
        ?.from('notes')
        ?.insert(insertPayload)
        ?.select('*, creator:user_profiles!notes_created_by_fkey(id, full_name, role)')
        ?.single();

      if (error) throw error;

      // Send mention notifications
      if (mentions?.length > 0 && data?.id) {
        await notesService?.sendMentionNotifications({
          noteId: data?.id,
          mentionedUserIds: mentions?.map(m => m?.id),
          authorName: profile?.full_name || 'A team member',
          noteText,
          entityType,
          entityId,
          companyId,
        });
      }

      return { data, error: null };
    } catch (err) {
      console.error('notesService.addNote error:', err);
      return { data: null, error: err };
    }
  },

  /**
   * Edit an existing note.
   */
  async editNote(noteId, { noteText, visibility, mentions = [] }) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      if (isDemoUser(user?.email)) {
        window.dispatchEvent(new CustomEvent('demo-mode-action'));
        return { error: { message: DEMO_ERROR_MESSAGE } };
      }

      const updatePayload = {
        note_text: noteText,
        visibility,
        note_type: visibility === 'client_shared' ? 'shared' : 'internal',
        mentions: mentions || [],
        updated_at: new Date()?.toISOString(),
      };

      const { data, error } = await supabase
        ?.from('notes')
        ?.update(updatePayload)
        ?.eq('id', noteId)
        ?.select('*, creator:user_profiles!notes_created_by_fkey(id, full_name, role)')
        ?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (err) {
      console.error('notesService.editNote error:', err);
      return { data: null, error: err };
    }
  },

  /**
   * Delete a note by ID.
   */
  async deleteNote(noteId) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      if (isDemoUser(user?.email)) {
        window.dispatchEvent(new CustomEvent('demo-mode-action'));
        return { error: { message: DEMO_ERROR_MESSAGE } };
      }

      const { error } = await supabase
        ?.from('notes')
        ?.delete()
        ?.eq('id', noteId);

      if (error) throw error;
      return { error: null };
    } catch (err) {
      console.error('notesService.deleteNote error:', err);
      return { error: err };
    }
  },

  /**
   * Get users on a site for @mentions picker.
   */
  async getSiteUsers(siteId, companyId) {
    try {
      const { data, error } = await supabase
        ?.from('user_profiles')
        ?.select('id, full_name, role')
        ?.eq('company_id', companyId)
        ?.eq('is_active', true)
        ?.in('role', ['admin', 'site_manager', 'worker'])
        ?.order('full_name');

      if (error) throw error;
      return { data: data || [], error: null };
    } catch (err) {
      console.error('notesService.getSiteUsers error:', err);
      return { data: [], error: err };
    }
  },

  /**
   * Send in-app notifications to mentioned users.
   *
   * Fire-and-forget: a failure here MUST NOT block the note save itself.
   * The note is already persisted by the time we get here, so the worst
   * case is a missing notification — log loudly (console.error) so prod
   * regressions are visible, but never throw.
   */
  async sendMentionNotifications({ noteId, mentionedUserIds, authorName, noteText, entityType, entityId, companyId }) {
    try {
      if (!mentionedUserIds?.length) return;

      // Polymorphic link routing. Match existing notification producers:
      // task_assigned ships /task-completion-workflow with no id (page handles
      // its own filtering). snag-detail-view reads ?id=. incident / near_miss
      // detail pages are list views without row-level deep-links, so we
      // surface their landing pages. Unknown entity types fall back to null
      // — notification still renders in the bell, just isn't clickable.
      const ENTITY_LINKS = {
        snag: (id) => `/snag-detail-view?id=${id}`,
        task: () => '/task-completion-workflow',
        incident: () => '/incident-reporting',
        near_miss: () => '/near-misses',
      };
      const linkUrl = ENTITY_LINKS?.[entityType]?.(entityId) || null;

      const notifications = mentionedUserIds?.map(userId => ({
        user_id: userId,
        company_id: companyId,
        type: 'mention',
        priority: 'medium',
        title: `${authorName} mentioned you in a note`,
        message: noteText?.length > 100 ? noteText?.substring(0, 100) + '…' : noteText,
        target_type: entityType,
        target_id: entityId,
        link_url: linkUrl,
        is_read: false,
      }));

      const { error } = await supabase?.from('notifications')?.insert(notifications);
      if (error) throw error;
    } catch (err) {
      console.error('sendMentionNotifications error:', err?.message);
    }
  },

  /**
   * Refresh a signed URL for a note photo (URLs expire).
   */
  async refreshPhotoUrl(storagePath) {
    try {
      const { data } = await supabase?.storage
        ?.from('note-photos')
        ?.createSignedUrl(storagePath, 60 * 60 * 24);
      return data?.signedUrl || null;
    } catch {
      return null;
    }
  },
};
